import clsx from 'clsx'
import { createElement, useLayoutEffect, useState, type HTMLAttributes } from 'react'
import Tooltip, { type TooltipProps } from '@/components/ui/tooltip/Tooltip'
import { kTooltipHoverDelayMs } from '@/constants/constants-ui'
import { useIsTouchOnlyDevice } from '@/hooks/useIsTouchOnlyDevice'

type TruncatedTextProps = Pick<TooltipProps, 'placement' | 'offsetX' | 'offsetY' | 'forceOpen' | 'hoverDelayMs'> &
  Omit<HTMLAttributes<HTMLElement>, 'title' | 'children'> & {
    /** The text. It stays on one line, and a tooltip shows all of it once it no longer fits. */
    children: string
    /**
     * The full text, when the rendered text is a shortened form of it. Defaults to the text itself, so the tooltip
     * only appears when CSS cuts the text off.
     */
    title?: string
    /** Element to render. A button gets `type="button"`. */
    as?: 'span' | 'button'
    /** Clips with `line-clamp-1` instead of `truncate`. */
    clamp?: boolean
    /**
     * Skips the tooltip on touch-only devices. There the tooltip toggles on tap and stops the click, which blocks a
     * parent button.
     */
    disableOnTouch?: boolean
  }

/**
 * The text's own width against the width its box gives it, both to a fraction of a pixel, for the text the whole-pixel
 * widths call equal: text that fits, and text cut off by less than a pixel, report the same number in both.
 */
const isTextWiderThanBox = (node: HTMLElement) => {
  const range = document.createRange()
  range.selectNodeContents(node)
  // Range measurement is a browser-only API; where it is missing, the whole-pixel widths stand on their own.
  const textWidth = range.getBoundingClientRect?.().width
  if (!textWidth) return false

  const style = getComputedStyle(node)
  const horizontalSpacing = ['paddingLeft', 'paddingRight', 'borderLeftWidth', 'borderRightWidth'] as const
  const boxWidth = horizontalSpacing.reduce((width, side) => width - parseFloat(style[side] || '0'), node.getBoundingClientRect().width)

  return textWidth > boxWidth
}

const isClipped = (node: HTMLElement) => node.scrollWidth > node.clientWidth || node.scrollHeight > node.clientHeight || isTextWiderThanBox(node)

/**
 * Single-line text that shows its full value in a tooltip only when the reader cannot see all of it: CSS cut it
 * off, or the caller rendered a shortened form of `title`.
 *
 * The tooltip waits for the cursor to settle, and by default its text lines up with the rendered text.
 */
export function TruncatedText({
  children,
  title = children,
  as = 'span',
  clamp = false,
  className,
  disableOnTouch = false,
  placement = 'top-start',
  offsetX = -10,
  offsetY = -10,
  forceOpen = false,
  hoverDelayMs = kTooltipHoverDelayMs,
  ...rest
}: TruncatedTextProps) {
  // The node lives in state so a remount (the tooltip wrapper toggling on and off) reattaches the observer.
  const [node, setNode] = useState<HTMLElement | null>(null)
  const [isTextClipped, setIsTextClipped] = useState(false)
  const isTouchOnly = useIsTouchOnlyDevice()

  // The observer covers the box changing. Text that changes inside the same box only shows in `scrollWidth`, so
  // the text re-measures too.
  useLayoutEffect(() => {
    if (!node) return
    const measure = () => setIsTextClipped(isClipped(node))
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(node)
    return () => observer.disconnect()
  }, [node, children])

  const isTextShortened = isTextClipped || title !== children
  const showTooltip = (isTextShortened || forceOpen) && !(disableOnTouch && isTouchOnly)

  return (
    <Tooltip
      title={showTooltip ? title : ''}
      placement={placement}
      offsetX={offsetX}
      offsetY={offsetY}
      forceOpen={forceOpen}
      hoverDelayMs={hoverDelayMs}
      // Plain text has nothing to interact with, and a bubble over a card would steal its hover state.
      disableInteractive
    >
      {createElement(
        as,
        {
          ...rest,
          ref: setNode,
          type: as === 'button' ? 'button' : undefined,
          // line-clamp sets its own display, so it takes the place of `block truncate` rather than stacking on it.
          className: clsx('min-w-0', clamp ? 'line-clamp-1' : 'block truncate', className),
        },
        children
      )}
    </Tooltip>
  )
}

export default TruncatedText
